import React, { useRef, useEffect, useState } from "react";
import { Box, Typography, Paper, IconButton, Divider } from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Close as CloseIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from "@mui/icons-material";
interface StyleProps {
  headerBgColor?: string;
  headerTextColor?: string;
  chatBgColor?: string;
  botMessageBgColor?: string;
  userMessageBgColor?: string;
  botTextColor?: string;
  userTextColor?: string;
  fontFamily?: string;
  fontSize?: {
    header?: string;
    message?: string;
    timestamp?: string;
    subheader?: string;
  };
  // ProfileIcon?: React.ComponentType<any>;
}

const defaultStyleProps: Required<StyleProps> = {
  headerBgColor: "#BE3455",
  headerTextColor: "white",
  chatBgColor: "white",
  botMessageBgColor: "white",
  userMessageBgColor: "white",
  botTextColor: "#343942",
  userTextColor: "#343942",
  fontFamily: "Lato, sans-serif",
  fontSize: {
    header: "1.25rem",
    message: "14px",
    timestamp: "0.7rem",
    subheader: "16px",
  },
  // ProfileIcon: () => null,
};

export interface Message {
  text: string;
  sender: "system" | "bot" | "user" | "agent" | "assistant";
  time?: string;
  choices?: string[];
  choiceType?: string;
  url?: string;
}

interface ChatBotProps {
  messages: Message[];
  inputText: string;
  setInputText: (text: string) => void;
  handleSend: () => void;
  handleOptionClick: (option: string) => void;
  isConnected: boolean;
  styleProps: Partial<StyleProps>;
  renderHeader: () => React.ReactNode;
  renderMessages: (messages: Message[]) => React.ReactNode;
  renderInputArea: () => React.ReactNode;
  disableInput: boolean;
  progressBar: React.ReactNode | null;
  congratulation: boolean;
  setCongratulation: any;
  onClose: () => void;
}

const ChatBot: React.FC<ChatBotProps> = ({
  messages,
  inputText,
  setInputText,
  handleSend,
  handleOptionClick,
  isConnected,
  styleProps: customStyleProps,
  renderHeader,
  renderMessages,
  renderInputArea,
  disableInput,
  progressBar,
  congratulation,
  setCongratulation,
  onClose,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true); // Track if at bottom of chat
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);

  const styleProps: Required<StyleProps> = {
    ...defaultStyleProps,
    ...customStyleProps,
    fontSize: {
      ...defaultStyleProps.fontSize,
      ...customStyleProps.fontSize,
    },
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToTop = () => {
    chatContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleScroll = () => {
    const scrollTop = chatContainerRef.current?.scrollTop || 0;
    const scrollHeight = chatContainerRef.current?.scrollHeight || 0;
    const clientHeight = chatContainerRef.current?.clientHeight || 0;

    setIsAtBottom(scrollTop + clientHeight >= scrollHeight - 10);

    if (scrollTop + clientHeight < scrollHeight) {
      setShouldScrollToBottom(false);
    } else {
      setShouldScrollToBottom(true);
    }
  };

  useEffect(() => {
    if (shouldScrollToBottom) {
      scrollToBottom();
    }
  }, [messages, shouldScrollToBottom]);

  const handleArrowClick = () => {
    if (isAtBottom) {
      scrollToTop();
    } else {
      scrollToBottom();
    }
  };

  const handleManualScroll = () => {
    const scrollTop = chatContainerRef.current?.scrollTop || 0;
    const scrollHeight = chatContainerRef.current?.scrollHeight || 0;
    const clientHeight = chatContainerRef.current?.clientHeight || 0;

    if (scrollTop + clientHeight < scrollHeight) {
      setShouldScrollToBottom(false);
    } else {
      setShouldScrollToBottom(true);
    }
  };

  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (chatContainer) {
      chatContainer.addEventListener("scroll", handleManualScroll);
    }

    return () => {
      if (chatContainer) {
        chatContainer.removeEventListener("scroll", handleManualScroll);
      }
    };
  }, []);

  return (
    <Paper
      elevation={3}
      sx={{
        width: 335,
        height: 470,
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        borderRadius: 4,
        marginTop: 5,
      }}
    >
      <Box
        sx={{
          bgcolor: styleProps.headerBgColor,
          color: styleProps.headerTextColor,
          width: 319,
          height: 60,
          px: 1,
          py: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
          {renderHeader()}
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: styleProps.headerTextColor,
            "&:hover": {
              backgroundColor: "transparent;",
              // color: "#fff",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ p: 1, textAlign: "center" }}>
        <Typography
          variant="caption"
          sx={{
            color: "#A7A7A7",
            fontSize: "12px",
            fontFamily: styleProps.fontFamily,
            fontWeight: 700,
          }}
        >
          Today
        </Typography>
      </Box>
      <Box
        ref={chatContainerRef}
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          bgcolor: styleProps.chatBgColor,
          p: 2,
          pb: 2,
        }}
        onScroll={handleScroll}
      >
        {renderMessages(messages)}
        <div ref={messagesEndRef} />
      </Box>

      <Divider
        sx={{
          borderColor: "rgba(0, 0, 0, 0.20)",
          width: "100%",
        }}
      />

      <Box
        sx={{
          pl: 2,
          pb: 2,
          pt: 2,
          display: "flex",
          alignItems: "center",
          height: 50,
          width: 304,
        }}
      >
        {renderInputArea()}
      </Box>
      <Box sx={{ position: "absolute", top: 53, right: 20 }}>
        <IconButton
          onClick={handleArrowClick}
          sx={{
            color: "#fff",
            backgroundColor: "#1c2a61",
            width: 30, 
            height: 30, 
            padding: 0, 
            "&:hover": {
              backgroundColor: "#0051cc",
            },
          }}
        >
          {isAtBottom ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
        </IconButton>
      </Box>
    </Paper>
  );
};

export default ChatBot;
